import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { PageHero } from "@/components/site/Layout";
import { EventEmptyState } from "@/components/site/EventEmptyState";
import { fetchEventCollection } from "@/lib/api";
import { useSelectedEvent } from "@/lib/event-context";
import { eventLabel } from "@/lib/events";
import { useState, useEffect } from "react";
import { Clock, MapPin, Users, Calendar, Coffee } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface AgendaItem {
  id: number;
  day: string;
  date: string;
  time_range: string;
  title: string;
  description: string;
  sort_order: number;
}

export const Route = createFileRoute("/agenda")({
  component: Agenda,
  head: () => ({
    meta: [
      { title: "Agenda — Arab Capital Markets Conference" },
      { name: "description", content: "Full conference programme and schedule." },
      { property: "og:title", content: "Agenda" },
      { property: "og:description", content: "Sessions and timings for the conference." },
    ],
  }),
});

function Agenda() {
  const { selectedEvent, selectedEventId, loading: eventsLoading } = useSelectedEvent();
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedEventId) {
      setAgenda([]);
      setLoading(false);
      return;
    }

    const loadAgenda = async () => {
      setLoading(true);
      try {
        const items = await fetchEventCollection<AgendaItem>(
          `/api/events/${selectedEventId}/agenda`,
          "/api/agenda",
        );
        setAgenda(items);
      } catch (error) {
        console.error("Failed to load agenda:", error);
        setAgenda([]);
      } finally {
        setLoading(false);
      }
    };

    loadAgenda();
  }, [selectedEventId]);

  // Group agenda items by day, preserving sort_order
  const groupedDays = (() => {
    const map = new Map<string, AgendaItem[]>();
    const sorted = [...agenda].sort((a, b) => {
      const dayA = (a.day as string | undefined) || "Day 1";
      const dayB = (b.day as string | undefined) || "Day 1";
      const dayDiff = dayA.localeCompare(dayB);
      if (dayDiff !== 0) return dayDiff;
      return a.sort_order - b.sort_order;
    });

    for (const item of sorted) {
      const day = (item.day as string | undefined) || "Day 1";
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(item);
    }

    return Array.from(map.entries());
  })();

  // Auto-select the first day once data loads
  useEffect(() => {
    if (groupedDays.length > 0 && activeDay === null) {
      setActiveDay(groupedDays[0][0]);
    }
    // Reset active day when event changes
    if (groupedDays.length === 0) {
      setActiveDay(null);
    }
  }, [groupedDays.length, selectedEventId]);

  const activeItems = groupedDays.find(([day]) => day === activeDay)?.[1] ?? [];

  const getIconForItem = (item: AgendaItem) => {
    const t = item.title.toLowerCase();
    if (t.includes("coffee") || t.includes("break")) return <Coffee className="w-5 h-5" />;
    if (t.includes("networking") || t.includes("lunch")) return <Users className="w-5 h-5" />;
    return <Users className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <>
        <PageHero
          eyebrow="Programme"
          title="Agenda"
          subtitle={selectedEvent ? eventLabel(selectedEvent) : "Select an event"}
        />
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center text-muted-foreground">Loading…</div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Programme"
        title="Agenda"
        subtitle={selectedEvent ? eventLabel(selectedEvent) : "Select an event"}
      />

      {!selectedEvent && !eventsLoading ? (
        <EventEmptyState
          title="Choose an event"
          message="Use the event selector in the header to view that agenda."
        />
      ) : agenda.length === 0 ? (
        <EventEmptyState
          title="No agenda yet"
          message="Sessions for this event have not been published."
        />
      ) : (
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto space-y-10">

            {/* Overview cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <Calendar className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                <div className="font-semibold text-lg text-blue-900">
                  {groupedDays.length} {groupedDays.length === 1 ? "Day" : "Days"}
                </div>
                <div className="text-sm text-blue-700 mt-1">Full programme</div>
              </Card>
              <Card className="p-6 text-center bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
                <Users className="w-8 h-8 mx-auto mb-3 text-emerald-600" />
                <div className="font-semibold text-lg text-emerald-900">Expert Speakers</div>
                <div className="text-sm text-emerald-700 mt-1">Industry leaders</div>
              </Card>
              <Card className="p-6 text-center bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                <MapPin className="w-8 h-8 mx-auto mb-3 text-purple-600" />
                <div className="font-semibold text-lg text-purple-900">
                  {selectedEvent?.location || "Venue"}
                </div>
                <div className="text-sm text-purple-700 mt-1">Premium venue</div>
              </Card>
            </div>

            {/* Day tabs */}
            {groupedDays.length > 1 && (
              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Conference days">
                {groupedDays.map(([day, items]) => {
                  const date = items[0]?.date;
                  const isActive = activeDay === day;
                  return (
                    <button
                      key={day}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveDay(day)}
                      className={[
                        "flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all",
                        isActive
                          ? "bg-accent text-white border-accent shadow-sm"
                          : "bg-white text-primary border-gray-200 hover:border-accent/60 hover:bg-accent/5",
                      ].join(" ")}
                    >
                      <Calendar className="w-4 h-4 shrink-0" />
                      <span>{day}</span>
                      {date && (
                        <span className={isActive ? "opacity-75 text-xs" : "text-muted-foreground text-xs"}>
                          {date}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Single day label when there is only one day */}
            {groupedDays.length === 1 && (
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-accent" />
                <span className="font-semibold text-primary text-lg">{groupedDays[0][0]}</span>
                {groupedDays[0][1][0]?.date && (
                  <Badge variant="outline">{groupedDays[0][1][0].date}</Badge>
                )}
              </div>
            )}

            {/* Timeline for the active day */}
            <div
              key={activeDay}
              className="relative"
              role="tabpanel"
              aria-label={activeDay ?? ""}
            >
              {/* Vertical line */}
              <div className="absolute left-8 sm:left-12 top-0 bottom-0 w-0.5 bg-gradient-to-b from-accent via-accent/50 to-transparent" />

              <div className="space-y-6">
                {activeItems.map((item) => (
                  <div
                    key={item.id}
                    className="relative flex items-start gap-4 sm:gap-8 group"
                  >
                    {/* Timeline dot */}
                    <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full bg-accent text-white shadow-md shrink-0">
                      {getIconForItem(item)}
                    </div>

                    {/* Content card */}
                    <Card className="flex-1 p-6 bg-white border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        {/* Time block */}
                        <div className="flex items-center gap-1.5 sm:w-36 shrink-0">
                          <Clock className="w-4 h-4 text-accent shrink-0" />
                          <span className="font-semibold text-accent text-sm">
                            {item.time_range}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 space-y-2">
                          <h3 className="font-semibold text-lg text-primary leading-snug">
                            {item.title}
                          </h3>
                          {item.description && (
                            <div
                              className="text-muted-foreground text-sm leading-relaxed prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            />
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>
      )}
    </>
  );
}
